import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Routes, Route } from 'react-router-dom';
import Header from './components/Header';
import Hero from './components/Hero';

// Lazy load non-critical components
const Services = lazy(() => import('./components/Services'));
const About = lazy(() => import('./components/About'));
const Contact = lazy(() => import('./components/Contact'));
const Footer = lazy(() => import('./components/Footer'));
const Feedback = lazy(() => import('./components/Feedback'));
const PorscheApp = lazy(() => import('./features/porsche/PorscheApp'));

const PorscheFallback = () => (
  <div className="min-h-screen bg-black flex flex-col items-center justify-center gap-4">
    <div className="w-12 h-12 border-4 border-porsche-red border-t-transparent rounded-full animate-spin" />
    <div className="text-white tracking-[0.3em] text-xs font-bold">INITIALIZING PORSCHE SYSTEMS</div>
  </div>
);

const HomePage: React.FC<{ isInitialized: boolean }> = ({ isInitialized }) => (
  <div className="min-h-screen bg-black text-white overflow-x-hidden">
    <Header />
    <Hero isInitialized={isInitialized} />
    <Suspense fallback={<div className="h-screen bg-black" />}>
      <Services />
      <About />
      <Contact />
      <Footer />
    </Suspense>
  </div>
);

const App: React.FC = () => {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setIsInitialized(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Routes>
      <Route
        path="/feedback"
        element={
          <Suspense fallback={<div className="min-h-screen bg-black" />}>
            <Feedback />
          </Suspense>
        }
      />
      <Route
        path="/porsche/*"
        element={
          <Suspense fallback={<PorscheFallback />}>
            <PorscheApp />
          </Suspense>
        }
      />
      <Route path="*" element={<HomePage isInitialized={isInitialized} />} />
    </Routes>
  );
};

export default App;
